import React, { useEffect, useRef, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// Actions
import {
  fetchAdminOrders,
  fetchAdminOrdersFilters,
  adminOrdersBulkActions,
} from '~/redux/actions/admin-orders-actions';
import { CLEAR_ADMIN_ORDERS_FILTERS } from '~/redux/actions/action-types';
import { success, failure } from '~/redux/actions/snackbar-actions';

//Utils
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { balanceFormatter, debounce, numberWithComma } from '~/utils/helperFunctions';

// Components
import Table from './Table';
import AdvanceFilters from './Filters';
import ActionModal from './ActionModal';
import DispatchNowModal from './DispatchNowModal';
import RescheduleOrderModal from './RescheduleOrderModal';
import NoAdminOrderScreen from './NoAdminOrderScreen';
import ExportModal from '~/components/Contacts/ExportModal';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// MUI Components
import { Box, Grid, Typography, useMediaQuery, Container } from '@mui/material';

// Icons
import { ExportModalCIcon, OrderCIcon, RevenueCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const AdminOrders = () => {
  // Use the useMediaQuery hook to check the screen size
  const { batchId, orderId } = useParams();
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const [isMounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openAdvanceFilters, setOpenAdvanceFilters] = useState(false);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [dispatchActionModal, setDispatchModal] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [exportModal, setExportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const childRef = useRef();
  const dispatch = useDispatch();

  const applyFilters = useSelector((state) => state.adminOrdersReducers.applyFilters);

  const filters = useSelector((state) => state.adminOrdersReducers.filters);
  const filterSize = useSelector((state) => state.adminOrdersReducers.filterSize);

  const totalOrders = useSelector((state) => state.adminOrdersReducers.totalOrders);

  const adminOrders = useSelector((state) => state.adminOrdersReducers.totalBatchesInDB);

  const totalRevenue = useSelector((state) => state.adminOrdersReducers.totalRevenue);

  const statsCards = [
    { id: 'orders', label: 'No. of Orders', value: numberWithComma(totalOrders), icon: OrderCIcon },
    {
      id: 'revenue',
      label: 'Total Revenue',
      value: balanceFormatter(totalRevenue),
      icon: RevenueCIcon,
    },
  ];

  const handleSearch = () => {
    if (search) {
      setPagination({ ...pagination, page: 0 });
      setSearchApplied(true);
      getAdminOrders();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const getAdminOrders = (orderId) => {
    let payload = {
      pagination,
      search: orderId ? orderId : search.length ? search : null,
      applyFilters: applyFilters,
      filters: applyFilters ? filters : null,
      batchId: batchId ? batchId : null,
    };
    dispatch(fetchAdminOrders(payload));
  };

  const exportOrders = async () => {
    try {
      setLoading(true);
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        search: orderId ? orderId : search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
        batchId: batchId ? batchId : null,
      };
      const response = await adminOrdersBulkActions(payload);
      getAdminOrders();
      setSelectedRows([]);
      dispatch(success(response?.data?.message || response?.message));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message));
    } finally {
      setExportModal(false);
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  useEffect(() => {
    if (isMounted) {
      getAdminOrders();
    }
  }, [pagination.page, pagination.pageSize, filterSize, batchId, filters]);

  useEffect(() => {
    if (!search && isMounted) {
      setSearchApplied(false);
      setSearch('');
      getAdminOrders();
    }
  }, [search]);

  useEffect(() => {
    setIsMounted(true);
    orderId && setSearch(orderId);
    orderId && setSearchApplied(true);
    orderId ? getAdminOrders(orderId) : getAdminOrders();
    dispatch(fetchAdminOrdersFilters());
    return () => {
      dispatch({ type: CLEAR_ADMIN_ORDERS_FILTERS });
    };
  }, []);

  const noAdminOrders = () => {
    return (
      <Container
        maxWidth={maxWidth}
        className="olcAdminEmptyPage"
        // sx={{
        //   margin: '120px auto 50px',
        // }}
      >
        <NoAdminOrderScreen />
      </Container>
    );
  };

  if (adminOrders === 0 && adminOrders !== null) {
    return noAdminOrders();
  }

  return (
    <>
      <Container maxWidth="xxl" className="olcAdminListPage">
        <div className="adminOrderMain olcAdminListContent">
          <Grid container className="olcAdminListToolbar olcAdminListToolbar--split">
            <Grid item lg={6} md={6} sm={12} xs={12} className="searchBtnWrapper">
              <Box className="searchWrapper">
                <OlcSearchInput
                  type="text"
                  value={search}
                  searchApplied={searchApplied}
                  onChange={(e) => {
                    if (e.target.value.length > 9) return;
                    setSearch(e.target.value.replace(/\s/g, '').replace(/\D/g, ''));
                  }}
                  onKeyDown={debouncedHandleKeyDown}
                  placeholder={`Search by order ID, user ID ${!batchId ? 'or batch ID' : ''}`}
                  inputProps={{ 'aria-label': 'Search by order ID, user ID, or batch ID' }}
                  onClear={() => setSearch('')}
                />
                <Box className="filterBtnWrapper">
                  <OlcButton
                    variant="secondary"
                    onClick={() => {
                      setOpenAdvanceFilters(true);
                    }}
                  >
                    Filters
                  </OlcButton>
                  {filterSize > 0 && (
                    <Typography className="filtersCounter">{filterSize}</Typography>
                  )}
                </Box>
                {filterSize > 0 && (
                  <OlcButton
                    variant="secondary"
                    onClick={() => {
                      childRef.current.clearFilters();
                    }}
                    className="clearFilterBtn"
                  >
                    {MESSAGES.ADMIN_DASHBOARD.ORDERS.CLEAR_FILTER_BTN_TEXT}
                  </OlcButton>
                )}
                {selectedRows.length > 0 && (
                  <OlcButton
                    variant="secondary"
                    onClick={() => {
                      setExportModal(true);
                    }}
                    startIcon={<ExportModalCIcon fill="#000" />}
                    className="adminOrderExportBtn"
                  >
                    {MESSAGES.ADMIN_DASHBOARD.ORDERS.EXPORT.EXPORT_BTN_TEXT}
                  </OlcButton>
                )}
              </Box>
            </Grid>
            <Grid item lg={6} md={6} sm={12} xs={12} className="searchBtnWrapper outer">
              {statsCards.map((item) => (
                <Box
                  key={item.id}
                  className="infoBox olcAdminStatCard"
                  aria-label={`${item.label}: ${item.value}`}
                >
                  <img src={item.icon} alt="" aria-hidden="true" className="infoBoxIcon" />
                  <p className="infoBoxValue">{item.value}</p>
                </Box>
              ))}
            </Grid>
          </Grid>
          <Table
            pagination={pagination}
            updatePagination={setPagination}
            setOpenActionModal={setOpenActionModal}
            setDispatchModal={setDispatchModal}
            setRescheduleModalOpen={setRescheduleModalOpen}
            setSelectedOrder={setSelectedOrder}
            searchApplied={searchApplied}
            setSelectedRows={setSelectedRows}
            selectedRows={selectedRows}
            activeRow={activeRow}
            setActiveRow={setActiveRow}
          />
          <AdvanceFilters
            open={openAdvanceFilters}
            handleCloseFilter={() => setOpenAdvanceFilters(false)}
            ref={childRef}
            updatePagination={setPagination}
          />
          <ActionModal
            openModal={openActionModal}
            handleCloseModal={() => setOpenActionModal(false)}
            selectedOrder={selectedOrder}
            getAdminOrders={getAdminOrders}
          />
          <DispatchNowModal
            openModal={dispatchActionModal}
            handleCloseModal={() => setDispatchModal(false)}
            selectedOrder={selectedOrder}
            getAdminOrders={getAdminOrders}
          />
          <RescheduleOrderModal
            openModal={rescheduleModalOpen}
            handleCloseModal={() => setRescheduleModalOpen(false)}
            selectedOrder={selectedOrder}
            getOrders={getAdminOrders}
            activeRow={activeRow}
            isAdmin={true}
          />
          <ExportModal
            open={exportModal}
            handleExport={exportOrders}
            handleClose={() => {
              setExportModal(false);
            }}
            loading={loading}
            header={
              selectedRows[0] === -1 || selectedRows.length > 1
                ? MESSAGES.ADMIN_DASHBOARD.ORDERS.EXPORT.MULTI_EXPORT_HEADER
                : MESSAGES.ADMIN_DASHBOARD.ORDERS.EXPORT.SINGLE_EXPORT_HEADER
            }
            message={
              selectedRows[0] === -1 || selectedRows.length > 1
                ? MESSAGES.ADMIN_DASHBOARD.ORDERS.EXPORT.EXPORT_ORDERS
                : MESSAGES.ADMIN_DASHBOARD.ORDERS.EXPORT.EXPORT_SINGLE_ORDER
            }
            subject={MESSAGES.ADMIN_DASHBOARD.ORDERS.EXPORT.EXPORT_MESSAGE}
          />
        </div>
      </Container>
    </>
  );
};

export default AdminOrders;
